<template>
    <div class="sentence-splitter" :class="{ 'sentence-splitter--disabled': disabled }">
        <div class="sentence-splitter__trigger">
            <slot :open="openModal">
                <button
                    type="button"
                    class="button button--blank sentence-control__button"
                    @click="openModal"
                >
                    <svg
                        width="18"
                        height="18"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512.016 512.016"
                    >
                        <path
                            fill="#ff6b06"
                            d="M208.008.008c-8.832 0-16 7.168-16 16v224h-64v-80c0-6.464-3.904-12.32-9.888-14.784-5.92-2.464-12.832-1.12-17.44 3.456l-96 96c-6.24 6.24-6.24 16.384 0 22.624l96 96a16.019 16.019 0 0011.328 4.704c2.048 0 4.16-.416 6.112-1.216 5.984-2.464 9.888-8.32 9.888-14.784v-80h64v224c0 8.832 7.168 16 16 16s16-7.168 16-16v-480c0-8.832-7.168-16-16-16zm-112 313.376l-57.376-57.376 57.376-57.376v114.752zM507.336 244.712l-96-96.032c-4.576-4.576-11.488-5.92-17.44-3.456-5.984 2.464-9.888 8.32-9.888 14.784v80h-64v-224c0-8.832-7.168-16-16-16s-16 7.168-16 16v480c0 8.832 7.168 16 16 16s16-7.168 16-16v-224h64v80c0 6.464 3.904 12.32 9.888 14.784 1.952.8 4.064 1.216 6.112 1.216 4.16 0 8.256-1.632 11.328-4.672l96-96c6.24-6.24 6.24-16.384 0-22.624zm-91.328 68.672V198.632l57.376 57.376-57.376 57.376z"
                        />
                    </svg>
                </button>
            </slot>
        </div>
        <portal v-if="showModal" to="modals">
            <Modal class="sentence-splitter__modal" name="sentence-splitter" @closed="closeModal">
                <template #head>
                    <div class="modal__title">
                        {{ $t('sentenceSplitter.splitSentence') }}
                    </div>
                </template>
                <template #content="{ close }">
                    <Spinner v-if="loading" class="spinner--opaque">{{ loadingText }}</Spinner>
                    <div class="sentence-splitter__text">
                        {{ $t('sentenceSplitter.selectPoint') }}
                    </div>
                    <div v-if="splitted.length > 1" class="sentence-splitter__field ssml-visual">
                        <template v-for="word in splitted" :key="word.id">
                            <span>
                                <div class="sentence-splitter__wrap">
                                    <!-- explicit spaces: the Vue 3 compiler drops the
                                         whitespace Vue 2 condensed around the word -->
                                    <span class="sentence-splitter__word">{{
                                        ' ' + word.word + ' '
                                    }}</span>
                                </div>
                                <div
                                    v-if="word.index !== splitted.length - 1"
                                    class="sentence-splitter__wrap"
                                >
                                    <span
                                        :class="{
                                            'is-active': isPointSelected(word),
                                        }"
                                        class="sentence-splitter__pause"
                                        @click="selectPoint(word)"
                                    />
                                </div>
                            </span>
                        </template>
                    </div>
                    <div v-else class="sentence-splitter__field">
                        {{ $t('sentenceSplitter.oneWord') }}
                    </div>
                    <div class="sentence-splitter__text">
                        {{ $t('sentenceSplitter.warning') }}
                    </div>
                </template>
                <template #bottom="{ close }">
                    <div class="modal__confirm">
                        <button
                            type="button"
                            :class="{ 'is-disabled': !selectedPoints.length }"
                            :disabled="!selectedPoints.length"
                            class="modal__confirm-button button"
                            @click="splitSentence"
                        >
                            {{ $t('sentenceSplitter.split') }}
                        </button>
                        <button
                            type="button"
                            class="modal__confirm-button button button--negative"
                            @click="close"
                        >
                            {{ $t('confirmModal.cancel') }}
                        </button>
                    </div>
                    <div style="margin-bottom: 10px">
                        <TransitionExpand>
                            <ErrorList v-if="error" :error="error" />
                        </TransitionExpand>
                    </div>
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { ErrorList } from '@speechki-frontend/shared/ui/forms';
import { makeid } from '@speechki-frontend/shared/helpers/functions';
import TransitionExpand from '@speechki-frontend/shared/ui/TransitionExpand';

import { splitBookpart } from '../../services/splitBookpart';

export default {
    name: 'SentenceSplitter',
    components: {
        Modal,
        ErrorList,
        TransitionExpand,
    },
    inject: ['selector', 'registerWsHandler', 'removeWsHandler'],
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showModal: false,
            loading: false,
            selectedPoints: [],
            error: null,
            loadingText: '',
        };
    },
    computed: {
        part() {
            if (!this.selector.selection.length) return null;

            return this.selector.sortedSelection[0].bookpart;
        },
        splitted() {
            if (!this.part) return '';

            return this.part.raw_text.split(/\s/).map((word, index) => {
                return {
                    word,
                    index,
                    id: makeid(),
                };
            });
        },
    },
    methods: {
        openModal() {
            this.showModal = true;
            this.$emit('opened');
        },

        closeModal() {
            this.showModal = false;
        },

        selectPoint(word) {
            if (this.isPointSelected(word)) {
                this.selectedPoints = this.selectedPoints.filter((w) => w !== word);
                return;
            }
            this.selectedPoints.push(word);
        },

        isPointSelected(word) {
            return this.selectedPoints.includes(word);
        },

        async splitSentence() {
            try {
                this.error = null;
                this.loading = true;

                const sentences = this.formSentences();

                this.loadingText = `Generating sentence 0 of ${sentences.length}`;

                await splitBookpart(this.part.id, sentences, ({ completed, total }) => {
                    this.loadingText = `Generating sentence ${completed} of ${total}`;
                });

                this.selector.clear();
                this.loading = false;
                this.$emit('split');
            } catch (e) {
                console.log(e);
                this.error = e;
                this.loading = true;
            }
        },

        formSentences() {
            const sentences = [];

            this.selectedPoints
                .sort((a, b) => a.index - b.index)
                .forEach((point, index, array) => {
                    sentences.push(
                        this.splitted
                            .slice(index > 0 ? array[index - 1].index + 1 : 0, point.index + 1)
                            .map((word) => word.word)
                            .join(' ')
                    );
                    if (index === array.length - 1) {
                        sentences.push(
                            this.splitted
                                .slice(point.index + 1)
                                .map((word) => word.word)
                                .join(' ')
                        );
                    }
                });

            return sentences;
        },
    },
};
</script>

<style lang="scss" src="./SentenceSplitter.scss"></style>
